import { Box, FormHelperText } from "@mui/material";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

const CODE_LENGTH = 6;

interface Props {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

/** One accessible input rendered as six large visual code cells. */
export default function OneTimeCodeInput({
  value,
  onChange,
  error = false,
  helperText,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: `repeat(${CODE_LENGTH}, minmax(0, 1fr))`,
          gap: { xs: 0.75, sm: 1.25 },
          "&:focus-within [data-code-cell]": {
            borderColor: error ? "error.main" : "nearBlack",
          },
        }}
      >
        {Array.from({ length: CODE_LENGTH }, (_, index) => (
          <Box
            key={index}
            data-code-cell
            aria-hidden
            sx={{
              minWidth: 0,
              aspectRatio: "1 / 1.08",
              display: "grid",
              placeItems: "center",
              border: "1px solid",
              borderColor: error ? "error.main" : "borderMuted",
              borderRadius: { xs: "10px", sm: "12px" },
              backgroundColor: "background.paper",
              color: "text.primary",
              fontSize: { xs: 24, sm: 30 },
              fontWeight: 500,
            }}
          >
            {value[index] ?? ""}
          </Box>
        ))}
        <Box
          component="input"
          type="text"
          aria-label={t("6-digit code")}
          value={value}
          autoFocus
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={CODE_LENGTH}
          onChange={handleChange}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
            padding: 0,
            opacity: 0,
            cursor: "text",
          }}
        />
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ marginX: 0, marginTop: 1 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}
